<template>
  <div v-if="ready" ref="container" class="sp-slot-cards-container">
    <div class="sp-slot-cards" :class="classModifiers">
      <EntryCard
        v-for="(entry, index) in visibleEntries"
        :key="index"
        :is-small-view="isSmallView"
        :entry="entry"
        :slot-variant="slotVariant"
        :more-button-variant="moreButtonVariant"
        :card-elevation="cardElevation"
        :show-year="showYear"
        @show-more-slots="showMoreSlots"
      />

      <div v-if="hasMoreEntries" class="sp-slot-cards__full-schedule">
        <div class="sp-slot-cards__full-schedule-content" :style="fullScheduleContentStyle">
          <template v-if="isBottom">
            <sp-button
              class="sp-slot-cards__full-schedule-button"
              :data-testid="TestId.slots.fullSchedule"
              data-analytics-element="slot-cards-full-schedule"
              data-analytics-interaction="click"
              color="primary"
              variant="text"
              @click="showMoreSlots"
            >
              <sp-icon slot="prepend" name="event-filled" size="small"></sp-icon>
              {{ t("full_schedule") }}
            </sp-button>
          </template>

          <template v-else>
            <sp-icon class="sp-slot-cards__full-schedule-icon" name="event-color" size="large"></sp-icon>
            <sp-button
              class="sp-slot-cards__full-schedule-link"
              :data-testid="TestId.slots.fullSchedule"
              data-analytics-element="slot-cards-full-schedule"
              data-analytics-interaction="click"
              variant="appearance-link"
              @click="showMoreSlots"
            >
              {{ t("full_schedule") }}
            </sp-button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useContainerBreakpoints } from "#ce/composables/useContainerBreakpoints";
import { toCssScaleProperty, toJSON } from "#ce/utils/props";
import { TestId } from "#/test-ids";
import { i18n, whenI18nReady } from "./i18n";
import EntryCard from "./components/EntryCard.ce.vue";
import { props as propsDefinitions } from "./props.js";

// Resolve strings against the deferred i18n instance directly (i18n.global.t)
// rather than useI18n(): each custom element gets its own app, and installing
// the vue-i18n plugin across the custom-element boundary doesn't reliably
// provide the instance to useI18n(). The ready gate below holds first paint
// until the catalog is hydrated, so this reads fully-loaded messages.
function t(...args) {
  return i18n.global.t(...args);
}

const emit = defineEmits(["show-more-slots"]);

// Gate first paint until the finder strings are hydrated so labels never flash
// their i18n keys before the catalog resolves.
const ready = ref(false);
onMounted(async () => {
  await whenI18nReady;
  ready.value = true;
});

const props = defineProps(propsDefinitions);

const container = useTemplateRef("container");
const {
  isXXSmall: isXXSmallContainer,
  isXSmall: isXSmallContainer,
  isSmall: isSmallContainer,
  isMedium: isMediumContainer,
  containerModifiers,
} = useContainerBreakpoints(container, {
  xxsmall: { max: 179 },
  xsmall: { min: 180, max: 299 },
  small: { min: 300, max: 449 },
  medium: { min: 450, max: 615 },
  large: { min: 616, max: 800 },
});

const rowGap = toCssScaleProperty(24);
const isSmallView = computed(
  () => isMediumContainer.value || isSmallContainer.value || isXSmallContainer.value || isXXSmallContainer.value,
);
const columnGap = computed(() => toCssScaleProperty(isSmallView.value ? 8 : 16));

/**
 * The prop `entries` as an array of objects.
 *
 * @type {import('vue').ComputedRef<Array<Object>>}
 */
const entries = computed(() => toJSON(props.entries, true));
const visibleEntries = computed(() => entries.value.slice(0, props.maxEntries));
const hasMoreEntries = computed(() => entries.value.length > visibleEntries.value.length);
const isBottom = computed(() => props.fullSchedulePlacement === "bottom");

const classModifiers = computed(() => [...containerModifiers.value, { "--is-bottom": isBottom.value }]);

/**
 * Inline style for the full schedule content wrapper.
 * Applies the fullScheduleDirection prop as flex-direction.
 */
const fullScheduleContentStyle = computed(() => ({
  flexDirection: props.fullScheduleDirection,
}));

function showMoreSlots({ date }) {
  emit("show-more-slots", { date });
}
</script>

<style lang="scss">
:host {
  display: block;
  width: 100%;
}
</style>

<style scoped lang="scss">
.sp-slot-cards-container {
  width: 100%;
  // Containment is required to prevent the component from growing beyond its parent's bounds.
  // Without it, the grid layout causes the container to overflow its allocated inline size.
  contain: inline-size;
}

.sp-slot-cards {
  display: grid;
  width: 100%;
  row-gap: v-bind(rowGap);
  column-gap: v-bind(columnGap);

  // Very big (default, >800px): 4 fixed-width columns, right-aligned
  grid-template-columns: repeat(4, var(--sp-comp-slot-cards-card-width, 12rem));
  justify-content: end;

  // Bottom placement: 3 fixed-width columns, right-aligned, Full Schedule spans below
  &.--is-bottom {
    grid-template-columns: repeat(3, var(--sp-comp-slot-cards-card-width, 12rem));
  }

  // Large (616–800px): 3 flexible columns, full width, Full Schedule in second row
  &.--is-large-container {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    :deep(.entry-card) {
      width: 100%;
    }
  }

  // Medium (450–615px): 3 flexible columns for compact cards, same as small
  &.--is-medium-container {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  // Small (300–449px): 3 flexible columns for compact cards, distributed
  &.--is-small-container {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  // Extra small (180–299px): 2 flexible columns for very compact layouts
  &.--is-x-small-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  // Extra extra small (≤179px): single column layout
  &.--is-xx-small-container {
    grid-template-columns: minmax(0, 1fr);
  }

  // --- Full Schedule cell ---
  &__full-schedule {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
  }

  // Full-width row layouts: Full Schedule spans all columns
  &.--is-large-container &__full-schedule,
  &.--is-medium-container &__full-schedule,
  &.--is-small-container &__full-schedule,
  &.--is-x-small-container &__full-schedule,
  &.--is-xx-small-container &__full-schedule {
    grid-column: 1 / -1;
  }

  // Bottom placement: Full Schedule always spans full width
  &.--is-bottom &__full-schedule {
    grid-column: 1 / -1;
  }

  // --- Full Schedule content wrapper ---
  &__full-schedule-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-ref-scale-8);
  }

  // --- Full Schedule icon (inline placement only) ---
  &__full-schedule-icon {
    display: var(--sp-comp-slot-cards-full-schedule-button-icon-display, block);
  }

  // Hide icon in small/xsmall layout
  &.--is-medium-container &__full-schedule-icon,
  &.--is-small-container &__full-schedule-icon,
  &.--is-x-small-container &__full-schedule-icon,
  &.--is-xx-small-container &__full-schedule-icon {
    display: none;
  }

  // --- Full Schedule link button (inline placement) ---
  &__full-schedule-link {
    text-align: var(--sp-comp-slot-cards-full-schedule-button-link-text-align, start);
    --sp-comp-button-font-weight: var(--sp-comp-slot-cards-full-schedule-button-link-font-weight);
  }

  // --- Full Schedule button (bottom placement) ---
  &__full-schedule-button {
    width: auto;
    --sp-comp-button-font-weight: var(--sp-comp-slot-cards-full-schedule-button-font-weight);
  }
}
</style>
